import { normalize } from 'normalizr';
import type { TreeNode } from 'tree-model';

import {
  type AssetsCollectionElasticSchemaWithIdNullable,
  type AssetsHttpGetCollectionsQuery,
  httpGetCollections,
  httpGetCollectionsByCollectionIdContents,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  httpPostSearch,
  SearchHttpPostSearch,
} from '@iconik-io/iconik-api/src/services/search';
import { OBJECT_TYPE_COLLECTIONS } from '@iconik-io/iconik-api/src/types/object_types';
import { entitySchemas } from '@iconik-io/shared-lib/src/redux/entities/schemas';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';

import { ID_COLLECTION as reducerId } from '../../constants/reducer_ids';
import type { AppDispatch, AppState, GetState } from '../../store/types';
import { addObjectsType } from '../../utils/object_type';
import { UUID_ZERO } from '../../utils/tree_handlers';
import { cancellable } from '../http/actions';
import {
  addBranches,
  addBranchesToNode,
  clearTree,
  modifyBranchByID,
  modifyBranchByNode,
} from '../tree/thunks';
import type { TreeReducerId, TreeReducerModel } from '../tree/types';

const fetchCollectionContents = (id: string, params = {}) =>
  httpGetCollectionsByCollectionIdContents(id, {
    per_page: 1000,
    object_types: 'collections',
    sort: 'title,asc',
    include_keyframes: false,
    ...params,
  });

export function getCollectionContents(
  id: string,
  params = {},
  collectionTreeReducerId?: TreeReducerId,
) {
  const treeReducerId = collectionTreeReducerId ?? reducerId;
  return (dispatch: AppDispatch) => {
    dispatch(modifyBranchByID(treeReducerId, id, { loading: true }));
    return dispatch(fetchCollectionContents(id, params))
      .then((response) => {
        const { objects, page, pages, total } = response.body;
        dispatch(
          addBranches(treeReducerId, {
            objects,
            parent: id,
            data: { total, page, pages },
          }),
        );
      })
      .finally(() => {
        dispatch(modifyBranchByID(treeReducerId, id, { loading: false }));
      });
  };
}

export function getCollectionContentsForNode(
  node: TreeNode<TreeReducerModel>,
  params = {},
  collectionTreeReducerId?: TreeReducerId,
) {
  const treeReducerId = collectionTreeReducerId ?? reducerId;
  return (dispatch: AppDispatch) => {
    dispatch(modifyBranchByNode(treeReducerId, node, { loading: true }));
    return dispatch(fetchCollectionContents(node.model.id, params))
      .then((response) => {
        const { objects, page, pages, total } = response.body;
        dispatch(
          addBranchesToNode(treeReducerId, node, {
            objects,
            data: { total, page, pages },
          }),
        );
      })
      .finally(() => {
        dispatch(modifyBranchByNode(treeReducerId, node, { loading: false }));
      });
  };
}

export function getCollections(
  data: AssetsHttpGetCollectionsQuery = {},
  collectionTreeReducerId?: TreeReducerId,
) {
  const treeReducerId = collectionTreeReducerId ?? reducerId;
  return (dispatch: AppDispatch): Promise<void> => {
    dispatch(
      modifyBranchByID(treeReducerId, UUID_ZERO, {
        loading: true,
      }),
    );
    return dispatch(
      httpGetCollections({
        per_page: 1000,
        ...data,
        sort: 'title,asc',
        is_root: 'true',
        status: 'ACTIVE',
        include_keyframes: false,
      }),
    )
      .then((response) => {
        const { objects, page, pages, total } = response.body;

        /**
         * TODO: We need to try to combine thunks collection_fetch and collections
         * into one thunks to avoid confusion. Also we need to investigate more
         * if we could avoid normalization and updating search_items entities
         * below. This part of code was initially added here in the commit
         * https://github.com/iconik-io/iconik_web/commit/51427b82af3
         * On the first glance it looks like a workaround for something
         * that we could remove
         */
        const { entities } = normalize(
          addObjectsType(objects, OBJECT_TYPE_COLLECTIONS),
          [entitySchemas.search_items],
        );
        dispatch(updateEntities(entities));

        if (!data.page || Number(data.page) === 1) {
          dispatch(clearTree(treeReducerId));
        }
        dispatch(
          addBranches(treeReducerId, { objects, data: { total, page, pages } }),
        );
      })
      .finally(() => {
        dispatch(
          modifyBranchByID(treeReducerId, UUID_ZERO, { loading: false }),
        );
      });
  };
}

export const selectRevealChain = (
  state: AppState,
  collectionId: string,
): string[] => state.entities.collections[collectionId]?.parents ?? [];

export const fetchChainChildren =
  (chain: string[]) => async (dispatch: AppDispatch) => {
    if (!chain.length) {
      return undefined;
    }

    const response = await dispatch(
      cancellable(
        httpPostSearch(
          {
            query: '',
            doc_types: ['collections'],
            filter: {
              operator: 'AND',
              terms: [
                { name: 'status', value: 'ACTIVE' },
                { name: 'in_collections', value_in: chain },
              ],
            },
            sort: [{ name: 'title', order: 'asc' }],
          },
          { save_search_history: false, per_page: 1000 },
        ),
        `${SearchHttpPostSearch}RevealCollection`,
      ),
    ).catch(() => undefined);

    // A truncated response cannot be applied: per-level totals are not
    // recoverable from a flat result set.
    if (!response || response.body.pages > 1) {
      return undefined;
    }

    const childrenByParent = new Map<
      string,
      AssetsCollectionElasticSchemaWithIdNullable[]
    >(chain.map((id) => [id, []]));
    response.body.objects.forEach((collection) => {
      collection.in_collections?.forEach((parentId) => {
        childrenByParent.get(parentId)?.push(collection);
      });
    });

    return childrenByParent;
  };

export const attachChainBranches =
  (
    chain: string[],
    childrenByParent:
      | Map<string, AssetsCollectionElasticSchemaWithIdNullable[]>
      | undefined,
  ) =>
  (dispatch: AppDispatch, getState: GetState) => {
    if (!childrenByParent) {
      return;
    }

    for (const ancestorId of chain) {
      const { treeRoot } = getState().collectionsTree.treeModel;
      const node = treeRoot.first((item) => item.model.id === ancestorId);
      if (!node) {
        return;
      }

      if (!node.children.length) {
        const objects = childrenByParent.get(ancestorId) ?? [];
        dispatch(
          addBranches(reducerId, {
            objects,
            parent: ancestorId,
            data: { total: objects.length, page: 1, pages: 1 },
          }),
        );
      }

      dispatch(modifyBranchByID(reducerId, ancestorId, { expanded: true }));
    }
  };
